const sticky = (() => {
  // =================================
  // 定数の定義
  // =================================
  const EVENT_KEY = '.sticky';

  const DEFAULTS = {
    // 追加のトップ余白（数値または関数）
    topSpacing: 0,
    // 追加のボトム余白（数値または関数）
    bottomSpacing: 0,
    // サイドバーのコンテナセレクタ（コンテナが指定されていない場合は false）
    containerSelector: false,
    // 内部ラッパーのセレクタ
    innerWrapperSelector: '.inner-wrapper-sticky',
    // 固定時に付与するCSSクラス
    stickyClass: 'is-affixed',
    // ResizeSensor の代わりに ResizeObserver を使う（両方使いたい場合はオプションで対応可）
    resizeSensor: true,
    // ウィンドウ幅がこの値以下の場合は固定を解除する
    minWidth: false
  };

  // =================================
  // sticky クラス
  // =================================
  class sticky {
    constructor(sidebar, options = {}) {
      // Object.assign を用いてオプションをマージ
      this.options = Object.assign({}, DEFAULTS, options);

      // サイドバー要素の取得（文字列の場合は querySelector を使用）
      this.sidebar = (typeof sidebar === 'string') ? document.querySelector(sidebar) : sidebar;
      if (!this.sidebar) {
        throw new Error("There is no specific sidebar element.");
      }

      this.sidebarInner = null;
      this.container = this.sidebar.parentElement;

      // 現在の固定状態を示すフラグ
      this.affixedType = 'STATIC';
      this.direction = 'down';
      this.support = {
        transform: false,
        transform3d: false
      };

      this._initialized = false;
      this._reStyle = false;
      this._breakpoint = false;
      this._running = false;

      // 各種寸法情報
      this.dimensions = {
        translateY: 0,
        maxTranslateY: 0,
        topSpacing: 0,
        lastTopSpacing: 0,
        bottomSpacing: 0,
        lastBottomSpacing: 0,
        sidebarHeight: 0,
        sidebarWidth: 0,
        containerTop: 0,
        containerHeight: 0,
        containerBottom: 0,
        viewportHeight: 0,
        viewportTop: 0,
        viewportBottom: 0,
        viewportLeft: 0,
        sidebarLeft: 0,
        lastViewportTop: 0
      };

      // this.handleEvent 内で this を参照できるようにバインド
      this.handleEvent = this.handleEvent.bind(this);

      // 初期化
      this.initialize();
    }

    initialize() {
      this._setSupportFeatures();

      // 内部ラッパーの取得（存在しなければ生成）
      if (this.options.innerWrapperSelector) {
        this.sidebarInner = this.sidebar.querySelector(this.options.innerWrapperSelector);
      }
      if (!this.sidebarInner) {
        const wrapper = document.createElement('div');
        wrapper.className = 'inner-wrapper-sticky';
        while (this.sidebar.firstChild) {
          wrapper.appendChild(this.sidebar.firstChild);
        }
        this.sidebar.appendChild(wrapper);
        this.sidebarInner = wrapper;
      }

      // コンテナ要素の決定（containerSelector が指定されている場合）
      if (this.options.containerSelector) {
        const containers = document.querySelectorAll(this.options.containerSelector);
        for (let container of containers) {
          if (container.contains(this.sidebar)) {
            this.container = container;
            break;
          }
        }
        if (!this.container) {
          throw new Error("The container does not contain the sidebar.");
        }
      }

      // topSpacing / bottomSpacing が関数でなければ数値に変換
      if (typeof this.options.topSpacing !== 'function')
        this.options.topSpacing = parseInt(this.options.topSpacing, 10) || 0;
      if (typeof this.options.bottomSpacing !== 'function')
        this.options.bottomSpacing = parseInt(this.options.bottomSpacing, 10) || 0;

      // minWidth によるブレークポイントチェック
      this._widthBreakpoint();

      // 各寸法の計算と初回の固定位置設定
      this.calcDimensions();
      this.stickyPosition();

      // イベントのバインド
      this.bindEvents();

      this._initialized = true;
    }

    bindEvents() {
      window.addEventListener('resize', this, { passive: true });
      window.addEventListener('scroll', this, { passive: true });
      this.sidebar.addEventListener('update' + EVENT_KEY, this);

      // ResizeObserver を使用（利用不可の場合は既存の ResizeSensor を利用）
      if (this.options.resizeSensor) {
        if (window.ResizeObserver) {
          this.resizeObserver = new ResizeObserver(this.handleEvent);
          this.resizeObserver.observe(this.sidebarInner);
          this.resizeObserver.observe(this.container);
        } else if (typeof ResizeSensor !== 'undefined') {
          new ResizeSensor(this.sidebarInner, this.handleEvent);
          new ResizeSensor(this.container, this.handleEvent);
        }
      }
    }

    handleEvent(event) {
      this.updateSticky(event);
    }

    calcDimensions() {
      if (this._breakpoint) return;
      const dims = this.dimensions;

      // コンテナの位置・高さ（getBoundingClientRect を利用）
      const containerOffset = sticky.offsetRelative(this.container);
      dims.containerTop = containerOffset.top;
      dims.containerHeight = this.container.clientHeight;
      dims.containerBottom = dims.containerTop + dims.containerHeight;

      // サイドバー内部ラッパーの寸法
      dims.sidebarHeight = this.sidebarInner.offsetHeight;
      dims.sidebarWidth = this.sidebarInner.offsetWidth;

      // ビューポートの寸法
      dims.viewportHeight = window.innerHeight;
      dims.maxTranslateY = dims.containerHeight - dims.sidebarHeight;

      // スクロールに依存する値の更新
      this._calcDimensionsWithScroll();
    }

    _calcDimensionsWithScroll() {
      const dims = this.dimensions;
      dims.sidebarLeft = sticky.offsetRelative(this.sidebar).left;

      dims.viewportTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop;
      dims.viewportHeight = window.innerHeight;
      dims.viewportBottom = dims.viewportTop + dims.viewportHeight;
      dims.viewportLeft = window.pageXOffset || document.documentElement.scrollLeft || document.body.scrollLeft;

      dims.topSpacing = (typeof this.options.topSpacing === 'function')
        ? parseInt(this.options.topSpacing(this.sidebar), 10) || 0
        : this.options.topSpacing;
      dims.bottomSpacing = (typeof this.options.bottomSpacing === 'function')
        ? parseInt(this.options.bottomSpacing(this.sidebar), 10) || 0
        : this.options.bottomSpacing;

      // 余白が前回から縮小している場合、translateY の値を調整
      if (this.affixedType === 'VIEWPORT-TOP') {
        if (dims.topSpacing < dims.lastTopSpacing) {
          dims.translateY += dims.lastTopSpacing - dims.topSpacing;
          this._reStyle = true;
        }
      } else if (this.affixedType === 'VIEWPORT-BOTTOM') {
        if (dims.bottomSpacing < dims.lastBottomSpacing) {
          dims.translateY += dims.lastBottomSpacing - dims.bottomSpacing;
          this._reStyle = true;
        }
      }
      dims.lastTopSpacing = dims.topSpacing;
      dims.lastBottomSpacing = dims.bottomSpacing;
    }

    isSidebarFitsViewport() {
      const dims = this.dimensions;
      const offset = this.direction === 'down' ? dims.lastBottomSpacing : dims.lastTopSpacing;
      return dims.sidebarHeight + offset < dims.viewportHeight;
    }

    observeScrollDir() {
      const dims = this.dimensions;
      if (dims.lastViewportTop === dims.viewportTop) return;

      // スクロール方向の変更を検知
      const furthest = this.direction === 'down' ? Math.min : Math.max;
      if (dims.viewportTop === furthest(dims.viewportTop, dims.lastViewportTop)) {
        this.direction = this.direction === 'down' ? 'up' : 'down';
      }
    }

    getAffixType() {
      this._calcDimensionsWithScroll();
      const dims = this.dimensions;
      const colliderTop = dims.viewportTop + dims.topSpacing;
      let affixType = this.affixedType;
      if (colliderTop <= dims.containerTop || dims.containerHeight <= dims.sidebarHeight) {
        dims.translateY = 0;
        affixType = 'STATIC';
      } else {
        affixType = (this.direction === 'up')
          ? this._getAffixTypeScrollingUp()
          : this._getAffixTypeScrollingDown();
      }

      // translateY の値を 0 ～ containerHeight 内に制限
      dims.translateY = Math.max(0, Math.min(dims.containerHeight, Math.round(dims.translateY)));
      dims.lastViewportTop = dims.viewportTop;
      return affixType;
    }

    _getAffixTypeScrollingDown() {
      const dims = this.dimensions;
      const sidebarBottom = dims.sidebarHeight + dims.containerTop;
      const colliderTop = dims.viewportTop + dims.topSpacing;
      const colliderBottom = dims.viewportBottom - dims.bottomSpacing;
      let affixType = this.affixedType;

      if (this.isSidebarFitsViewport()) {
        if (dims.sidebarHeight + colliderTop >= dims.containerBottom) {
          dims.translateY = dims.containerBottom - sidebarBottom;
          affixType = 'CONTAINER-BOTTOM';
        } else if (colliderTop >= dims.containerTop) {
          dims.translateY = colliderTop - dims.containerTop;
          affixType = 'VIEWPORT-TOP';
        }
      } else {
        if (dims.containerBottom <= colliderBottom) {
          dims.translateY = dims.containerBottom - sidebarBottom;
          affixType = 'CONTAINER-BOTTOM';
        } else if (sidebarBottom + dims.translateY <= colliderBottom) {
          dims.translateY = colliderBottom - sidebarBottom;
          affixType = 'VIEWPORT-BOTTOM';
        } else if (
          dims.containerTop + dims.translateY <= colliderTop &&
          dims.translateY !== 0 &&
          dims.maxTranslateY !== dims.translateY
        ) {
          affixType = 'VIEWPORT-UNBOTTOM';
        }
      }
      return affixType;
    }

    _getAffixTypeScrollingUp() {
      const dims = this.dimensions;
      const sidebarBottom = dims.sidebarHeight + dims.containerTop;
      const colliderTop = dims.viewportTop + dims.topSpacing;
      const colliderBottom = dims.viewportBottom - dims.bottomSpacing;
      let affixType = this.affixedType;

      if (colliderTop <= dims.translateY + dims.containerTop) {
        dims.translateY = colliderTop - dims.containerTop;
        affixType = 'VIEWPORT-TOP';
      } else if (dims.containerBottom <= colliderBottom) {
        dims.translateY = dims.containerBottom - sidebarBottom;
        affixType = 'CONTAINER-BOTTOM';
      } else if (!this.isSidebarFitsViewport()) {
        if (dims.containerTop <= colliderTop && dims.translateY !== 0 && dims.maxTranslateY !== dims.translateY) {
          affixType = 'VIEWPORT-UNBOTTOM';
        }
      }
      return affixType;
    }

    _getStyle(affixType) {
      const dims = this.dimensions;
      let style = { inner: {}, outer: {} };

      switch (affixType) {
        case 'VIEWPORT-TOP':
          style.inner = {
            position: 'fixed',
            top: dims.topSpacing + 'px',
            left: (dims.sidebarLeft - dims.viewportLeft) + 'px',
            width: dims.sidebarWidth + 'px'
          };
          break;
        case 'VIEWPORT-BOTTOM':
          style.inner = {
            position: 'fixed',
            top: 'auto',
            left: dims.sidebarLeft + 'px',
            bottom: dims.bottomSpacing + 'px',
            width: dims.sidebarWidth + 'px'
          };
          break;
        case 'CONTAINER-BOTTOM':
        case 'VIEWPORT-UNBOTTOM': {
          // _getTranslate のパラメータは (x, y, z)
          const translate = this._getTranslate(0, dims.translateY + 'px', 0);
          if (translate) {
            style.inner = { transform: translate, position: 'absolute', top: '0', width: dims.sidebarWidth + 'px' };
          } else {
            style.inner = { position: 'absolute', top: dims.translateY + 'px', width: dims.sidebarWidth + 'px' };
          }
          break;
        }
      }

      // 固定状態の場合、ラッパー要素に高さと相対配置を設定
      if (['VIEWPORT-TOP', 'VIEWPORT-BOTTOM', 'VIEWPORT-UNBOTTOM', 'CONTAINER-BOTTOM'].includes(affixType)) {
        style.outer = { height: dims.sidebarHeight + 'px', position: 'relative' };
      }

      // Object.assign を使ってデフォルト値とマージ
      style.outer = Object.assign({ height: '', position: '' }, style.outer);
      style.inner = Object.assign({ position: 'relative', top: '', left: '', bottom: '', width: '', transform: '' }, style.inner);

      return style;
    }

    stickyPosition(force = false) {
      if (this._breakpoint) return;
      force = this._reStyle || force;
      const affixType = this.getAffixType();
      const style = this._getStyle(affixType);

      if (this.affixedType !== affixType || force) {
        const eventName = 'affix.' + affixType.toLowerCase().replace('viewport-', '') + EVENT_KEY;
        sticky.eventTrigger(this.sidebar, eventName);

        if (affixType === 'STATIC') {
          sticky.removeClass(this.sidebar, this.options.stickyClass);
        } else {
          sticky.addClass(this.sidebar, this.options.stickyClass);
        }

        // 外側ラッパーと内部ラッパーにスタイルを適用
        for (const key in style.outer) {
          this.sidebar.style[key] = style.outer[key];
        }
        for (const key in style.inner) {
          this.sidebarInner.style[key] = style.inner[key];
        }

        const affixedEventName = 'affixed.' + affixType.toLowerCase().replace('viewport-', '') + EVENT_KEY;
        sticky.eventTrigger(this.sidebar, affixedEventName);
      } else {
        if (this._initialized) {
          this.sidebarInner.style.left = style.inner.left;
        }
      }
      this.affixedType = affixType;
      this._reStyle = false;
    }

    _widthBreakpoint() {
      if (this.options.minWidth && window.innerWidth <= this.options.minWidth) {
        this._breakpoint = true;
        this.affixedType = 'STATIC';
        this.sidebar.removeAttribute('style');
        sticky.removeClass(this.sidebar, this.options.stickyClass);
        this.sidebarInner.removeAttribute('style');
      } else {
        this._breakpoint = false;
      }
    }

    updateSticky(event = {}) {
      if (this._running) return;
      this._running = true;
      requestAnimationFrame(() => {
        switch (event.type) {
          case 'scroll':
            this._calcDimensionsWithScroll();
            this.observeScrollDir();
            this.stickyPosition();
            break;
          case 'resize':
          default:
            this._widthBreakpoint();
            this.calcDimensions();
            this.stickyPosition(true);
            break;
        }
        this._running = false;
      });
    }

    _setSupportFeatures() {
      // CSS.supports API を使い、transform/translate3d のサポートを判定
      this.support.transform = sticky.supportTransform(false);
      this.support.transform3d = sticky.supportTransform(true);
    }

    // パラメータは (x, y, z) の順（呼び出し側で合わせてください）
    _getTranslate(x = 0, y = 0, z = 0) {
      if (this.support.transform3d) return `translate3d(${x}, ${y}, ${z})`;
      else if (this.support.transform) return `translate(${x}, ${y})`;
      return false;
    }

    destroy() {
      window.removeEventListener('resize', this, { passive: true });
      window.removeEventListener('scroll', this, { passive: true });
      this.sidebar.removeEventListener('update' + EVENT_KEY, this);

      sticky.removeClass(this.sidebar, this.options.stickyClass);
      this.sidebar.style.minHeight = '';

      const styleResetInner = { position: '', top: '', left: '', bottom: '', width: '', transform: '' };
      const styleResetOuter = { height: '', position: '' };

      for (const key in styleResetOuter) {
        this.sidebar.style[key] = styleResetOuter[key];
      }
      for (const key in styleResetInner) {
        this.sidebarInner.style[key] = styleResetInner[key];
      }

      if (this.options.resizeSensor) {
        if (this.resizeObserver) {
          this.resizeObserver.disconnect();
        } else if (typeof ResizeSensor !== 'undefined') {
          ResizeSensor.detach(this.sidebarInner, this.handleEvent);
          ResizeSensor.detach(this.container, this.handleEvent);
        }
      }
    }

    // =====================================
    // 静的メソッド
    // =====================================
    static supportTransform(transform3d = false) {
      if (typeof CSS !== 'undefined' && CSS.supports) {
        return transform3d
          ? CSS.supports('transform', 'translate3d(0,0,0)')
          : CSS.supports('transform', 'translate(0,0)');
      }
      const property = transform3d ? 'perspective' : 'transform';
      const prefixes = ['', 'Webkit', 'Moz', 'O', 'ms'];
      const testElem = document.createElement('div');
      for (let prefix of prefixes) {
        const prop = prefix ? prefix + property.charAt(0).toUpperCase() + property.slice(1) : property;
        if (prop in testElem.style) return true;
      }
      return false;
    }

    static eventTrigger(element, eventName, data) {
      let event;
      if (typeof CustomEvent === 'function') {
        event = new CustomEvent(eventName, { detail: data });
      } else {
        event = document.createEvent('CustomEvent');
        event.initCustomEvent(eventName, true, true, data);
      }
      element.dispatchEvent(event);
    }

    // getBoundingClientRect を用いて座標を取得（ドキュメント座標に変換）
    static offsetRelative(element) {
      const rect = element.getBoundingClientRect();
      return { top: rect.top + window.pageYOffset, left: rect.left + window.pageXOffset };
    }

    // クラス操作は modern ブラウザ向けに classList を使用
    static addClass(element, className) {
      element.classList.add(className);
    }

    static removeClass(element, className) {
      element.classList.remove(className);
    }

    static hasClass(element, className) {
      return element.classList.contains(className);
    }

    static get defaults() {
      return DEFAULTS;
    }
  }

  return sticky;
})();

// ES モジュールとしてエクスポートし、グローバルにも公開
export default sticky;
window.sticky = sticky;